import {
  endAt,
  get,
  limitToLast,
  onValue,
  orderByKey,
  query,
  ref,
  remove,
  set,
  startAt,
} from "firebase/database";
import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ConfirmDialog from "../components/ConfirmDialog";
import { useFirebase } from "../FbContex";
import { db } from "../firebase";
import {
  Row,
  dayList,
  downsample,
  parseDay,
  ticksFor,
  wibClock,
} from "../lib/history";
import {
  DEFAULT_THRESHOLDS,
  Range,
  SeriesPoint,
  TERAS_NAME_KEY,
  buildSeries,
  daysFor,
  humKeys,
  isHumKey,
  levelOf,
  mergeSeries,
  liveSensors,
  parseSensors,
  sensorKeys,
  seriesLabel,
  statsFor,
  windowFor,
} from "../lib/temperature";

/**
 * Read-only view of /data/suhuN and /history/temperature/<day>; the board is the
 * only writer there. The one thing this page writes is its own config under
 * /settings (sensor names keyed by ROM address, warning thresholds).
 * History is one `get()` per day, never a listener on the parent.
 */

const MAX_POINTS = 1500;
const RANGES: Range[] = ["1h", "6h", "24h", "7d"];
const RANGE_LABEL: Record<Range, string> = {
  "1h": "1 jam",
  "6h": "6 jam",
  "24h": "24 jam",
  "7d": "7 hari",
};
const NAMES_PATH = "settings/temperature_names";
const THRESH_PATH = "settings/temperature_thresholds";

const CSS = `
.tmp{--c7:#0f9bb5;--c1:#2a78d6;--c2:#eb6834;--c3:#1baf7a;--c4:#eda100;--c5:#e34948;--c6:#8e5bd6;--grid:rgba(120,120,120,.22);--ink:#52514e}
.dark .tmp{--c7:#38b2cc;--c1:#3987e5;--c2:#d95926;--c3:#199e70;--c4:#c98500;--c5:#e66767;--c6:#a67ae6;--ink:#c3c2b7}
.tmp .recharts-cartesian-axis-tick-value{fill:var(--ink)}
`;
// DS18B20 u1..u6 take c1..c6; the teras DHT (t1, rh1) has its own slot.
const colorOf = (key: string) =>
  key[0] === "u" ? `var(--c${((Number(key.slice(1)) - 1) % 6) + 1})` : "var(--c7)";
const LEVEL_STYLE = {
  ok: "border-slate-500/50",
  warn: "border-yellow-500 bg-yellow-500/10",
  crit: "border-red-500 bg-red-500/10",
};
const hhmm = (t: number) => wibClock(t).slice(0, 5);
const fmt1 = (x: number) => x.toFixed(1).replace(".", ",");
const btn = "rounded border border-slate-500/50 px-2 py-1 text-sm hover:bg-slate-500/20";

function useValue<T>(path: string, parse: (v: unknown) => T, init: T) {
  const [val, setVal] = React.useState<T>(init);
  const [err, setErr] = React.useState("");
  React.useEffect(
    () =>
      onValue(
        ref(db, path),
        (ss) => setVal(parse(ss.val())),
        (e) => setErr(e.message)
      ),
    // parse is a module-level function at every call site
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path]
  );
  return [val, err] as const;
}

const parseNames = (v: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (v && typeof v === "object")
    for (const [k, n] of Object.entries(v)) if (typeof n === "string") out[k] = n;
  return out;
};
const parseThresholds = (v: unknown) => {
  const o = (v ?? {}) as Record<string, unknown>;
  const n = (x: unknown, d: number) => (typeof x === "number" && Number.isFinite(x) ? x : d);
  return {
    warn: n(o.warn, DEFAULT_THRESHOLDS.warn),
    crit: n(o.crit, DEFAULT_THRESHOLDS.crit),
  };
};

function NameEditor({
  label,
  rom,
  onSave,
}: {
  label: string;
  rom?: string;
  onSave: (rom: string, name: string) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState("");
  if (!rom) return <span className="font-semibold">{label}</span>;
  if (!editing)
    return (
      <button
        type="button"
        className="font-semibold text-left hover:underline"
        title="Ubah nama"
        onClick={() => {
          setText(label.startsWith("Sensor ") ? "" : label);
          setEditing(true);
        }}
      >
        {label} <i className="fas fa-pen text-xs opacity-50" />
      </button>
    );
  const done = (save: boolean) => {
    setEditing(false);
    if (save) onSave(rom, text.trim());
  };
  return (
    <input
      autoFocus
      value={text}
      maxLength={24}
      placeholder="Nama sensor"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => done(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") done(true);
        if (e.key === "Escape") done(false);
      }}
      className="w-full rounded border border-slate-500/50 bg-transparent px-1 text-sm"
    />
  );
}

function Tip({
  active,
  payload,
  label,
  names,
}: {
  active?: boolean;
  payload?: any[];
  label?: number;
  names: Record<string, string>;
}) {
  const rows = (active ? payload : [])?.filter((p) => p.value !== undefined) ?? [];
  if (!rows.length) return null;
  return (
    <div className="rounded border border-slate-500/50 bg-white dark:bg-slate-800 px-2 py-1 text-xs shadow">
      <div className="font-semibold">{wibClock(label as number)} WIB</div>
      {rows.map((p) => (
        <div key={p.dataKey}>
          {names[p.dataKey]}:{" "}
          {isHumKey(p.dataKey) ? `${Math.round(Number(p.value))} %` : `${fmt1(Number(p.value))} °C`}
        </div>
      ))}
    </div>
  );
}

function TerasCard({
  level,
  title,
  value,
  t,
}: {
  level: "ok" | "warn" | "crit";
  title: React.ReactNode;
  value: string;
  t: number;
}) {
  const stale = Date.now() / 1000 - t > 120;
  return (
    <div className={`rounded border p-3 ${LEVEL_STYLE[level]}`}>
      <div className="flex items-center gap-1 text-sm">
        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: "var(--c7)" }} />
        {title}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className={`text-xs ${stale ? "text-yellow-500" : "opacity-60"}`}>
        {stale ? "data lama · " : ""}
        {wibClock(t)} WIB
      </div>
    </div>
  );
}

export default function Temperature() {
  const { state, sources } = useFirebase();
  const [roms] = useValue("history/temperature/sensors", parseSensors, {});
  const [names, namesErr] = useValue(NAMES_PATH, parseNames, {});
  const [thr] = useValue(THRESH_PATH, parseThresholds, DEFAULT_THRESHOLDS);
  const [saveErr, setSaveErr] = React.useState("");

  const days = React.useMemo(() => dayList(Date.now()), []);
  const [day, setDay] = React.useState(days[0]);
  const [range, setRange] = React.useState<Range>("6h");
  const [rows, setRows] = React.useState<Row[]>([]);
  const [terasRows, setTerasRows] = React.useState<Row[]>([]);
  const [terasErr, setTerasErr] = React.useState("");
  const [status, setStatus] = React.useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = React.useState("");
  const [hidden, setHidden] = React.useState<Set<string>>(new Set());
  const [tick, setTick] = React.useState(0);
  const req = React.useRef(0);

  const live = liveSensors(state.data);
  const labelOf = (i: number) => seriesLabel("u" + i, roms, names);

  // Teras DHT: history rows only (no /data key), so the "live" card is the newest
  // row of today's bucket. limitToLast(1) keeps this to one row, not a day.
  const [terasLive, setTerasLive] = React.useState<Row | undefined>();
  React.useEffect(() => {
    const load = () =>
      get(query(ref(db, "history/teras/" + dayList(Date.now())[0]), orderByKey(), limitToLast(1)))
        .then((ss) => setTerasLive(parseDay(ss.val()).pop()))
        .catch(() => setTerasLive(undefined));
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [tick]);

  // `tick` re-evaluates "now" on each manual refresh; the window is fixed per load.
  const win = React.useMemo(
    () => windowFor(range, day, days[0], Math.floor(Date.now() / 1000)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, day, days, tick]
  );
  const need = React.useMemo(() => daysFor(win[0], win[1], days), [win, days]);
  const needKey = need.join(",");

  React.useEffect(() => {
    const me = ++req.current; // a slow earlier pick must not overwrite a newer one
    setStatus("loading");
    // Teras rows are 5 s apart (~17k/day): fetch only the window, by key range, and
    // skip it for 7 days (~24 MB of RTDB egress per click).
    const terasP =
      range === "7d"
        ? Promise.resolve([] as Row[])
        : Promise.all(
            need.map((d) =>
              get(
                query(
                  ref(db, "history/teras/" + d),
                  orderByKey(),
                  startAt(String(Math.floor(win[0]))),
                  endAt(String(Math.ceil(win[1])))
                )
              )
            )
          ).then((snaps) => snaps.flatMap((s) => parseDay(s.val())));
    // A teras failure (e.g. rules) must not hide the DS18B20 history.
    const terasSafe = terasP
      .then((r) => ({ rows: r, err: "" }))
      .catch((e) => ({ rows: [] as Row[], err: String(e?.message ?? e) }));
    Promise.all([Promise.all(need.map((d) => get(ref(db, "history/temperature/" + d)))), terasSafe])
      .then(([snaps, teras]) => {
        if (me !== req.current) return;
        setRows(snaps.flatMap((s) => parseDay(s.val())).sort((a, b) => a.t - b.t));
        setTerasRows(teras.rows.sort((a, b) => a.t - b.t));
        setTerasErr(teras.err);
        setStatus("ok");
      })
      .catch((e) => {
        if (me !== req.current) return;
        setRows([]);
        setError(String(e?.message ?? e));
        setStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needKey, tick]);

  // Stats come from each source's own samples; the merged series is for drawing
  // only (it repeats held values, which would skew an average).
  const sensSeries = React.useMemo(() => buildSeries(rows, win[0], win[1]), [rows, win]);
  const terasSeries = React.useMemo(() => buildSeries(terasRows, win[0], win[1]), [terasRows, win]);
  const series = React.useMemo(
    () => mergeSeries([sensSeries, terasSeries]),
    [sensSeries, terasSeries]
  );
  const keys = React.useMemo(() => sensorKeys(series), [series]);
  const hums = React.useMemo(() => humKeys(series), [series]);
  const chartData = React.useMemo(() => {
    const force: number[] = [];
    series.forEach((p, i) => {
      if (p.gap) force.push(i - 1, i, i + 1); // gap markers
    });
    return downsample(series.length, MAX_POINTS, force).map((i) => series[i]) as SeriesPoint[];
  }, [series]);
  const nameByKey = React.useMemo(() => {
    const o: Record<string, string> = {};
    [...keys, ...hums].forEach((k) => (o[k] = seriesLabel(k, roms, names)));
    return o;
  }, [keys, hums, roms, names]);
  const statsSrc = (k: string) => (k[0] === "u" ? sensSeries : terasSeries);
  const lastRow = [rows[rows.length - 1], terasRows[terasRows.length - 1]]
    .filter(Boolean)
    .sort((a, b) => b.t - a.t)[0];

  const write = (p: Promise<void>) =>
    p.then(() => setSaveErr("")).catch((e) => setSaveErr(String(e?.message ?? e)));
  const saveName = (rom: string, name: string) =>
    write(name ? set(ref(db, `${NAMES_PATH}/${rom}`), name) : remove(ref(db, `${NAMES_PATH}/${rom}`)));
  const saveThr = (k: "warn" | "crit", v: string) => {
    const n = Number(v.replace(",", "."));
    if (v.trim() && Number.isFinite(n)) write(set(ref(db, `${THRESH_PATH}/${k}`), n));
  };

  // Reset board
  const boards = Object.entries(sources).filter(([, s]) => s.ip);
  const [boardId, setBoardId] = React.useState("");
  const [confirm, setConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<{ ok: boolean; msg: string } | null>(null);
  const board = boards.find(([id]) => id === boardId) ?? boards[0];
  const mixed = window.location.protocol === "https:";

  const reset = async () => {
    if (!board) return;
    const url = `http://${board[1].ip}/reset`;
    setBusy(true);
    const ctl = new AbortController();
    const to = setTimeout(() => ctl.abort(), 6000);
    try {
      const r = await fetch(url, { method: "POST", signal: ctl.signal });
      const body = (await r.text()).slice(0, 120);
      setResult({ ok: r.ok, msg: `HTTP ${r.status}${body ? " · " + body : ""}` });
    } catch (e: any) {
      if (e?.name === "AbortError")
        setResult({ ok: false, msg: "Timeout: board tidak menjawab (bukan di jaringan lokal?)." });
      else {
        // Likely CORS: the request may still have reached the board, we just can't read it.
        try {
          await fetch(url, { method: "POST", mode: "no-cors", signal: ctl.signal });
          setResult({ ok: true, msg: "Terkirim, tetapi respons tidak terbaca (CORS)." });
        } catch {
          setResult({
            ok: false,
            msg: mixed
              ? "Gagal: halaman HTTPS tidak boleh memanggil http:// (mixed content). Buka app lewat http:// di jaringan lokal."
              : "Gagal terhubung. Pastikan perangkat ada di jaringan lokal yang sama dengan board.",
          });
        }
      }
    } finally {
      clearTimeout(to);
      setBusy(false);
      setConfirm(false);
    }
  };

  const toggle = (k: string) =>
    setHidden((h) => {
      const n = new Set(h);
      if (!n.delete(k)) n.add(k);
      return n;
    });

  return (
    <div className="tmp px-2 pb-10">
      <style>{CSS}</style>
      <h1 className="text-lg font-bold mb-3">Temperature</h1>

      {namesErr && (
        <div className="rounded border border-red-500/50 bg-red-500/10 p-3 text-sm mb-3">
          Gagal membaca <code>/{NAMES_PATH}</code>: {namesErr}
        </div>
      )}
      {saveErr && (
        <div className="rounded border border-red-500/50 bg-red-500/10 p-3 text-sm mb-3">
          Gagal menyimpan: {saveErr}
          {/permission/i.test(saveErr) && (
            <div className="mt-1 opacity-80">
              Rule RTDB untuk <code>/settings</code> belum mengizinkan tulis. Rule diatur di
              Firebase console, bukan di repo ini.
            </div>
          )}
        </div>
      )}

      {live.length === 0 && terasLive?.temps.t1 === undefined && terasLive?.hum?.rh1 === undefined ? (
        <div className="py-6 text-center opacity-70 mb-3">
          Belum ada data suhu terkini di <code>/data/suhuN</code>.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
          {live.map((s) => {
            const lv = levelOf(s.value, thr);
            return (
              <div key={s.index} className={`rounded border p-3 ${LEVEL_STYLE[lv]}`}>
                <div className="flex items-center gap-1 text-sm">
                  <span
                    className="inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ background: colorOf("u" + s.index) }}
                  />
                  <NameEditor label={labelOf(s.index)} rom={roms[s.index]} onSave={saveName} />
                </div>
                <div className="text-2xl font-bold mt-1">
                  {s.value === null ? (
                    <span className="opacity-60" title="Sensor putus">
                      -- <span className="text-xs font-normal">putus</span>
                    </span>
                  ) : (
                    <>
                      {fmt1(s.value)} °C
                      {lv !== "ok" && (
                        <span className="text-xs font-normal ml-2">
                          {lv === "crit" ? "panas!" : "hangat"}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {terasLive && terasLive.temps.t1 !== undefined && (
            <TerasCard
              level={levelOf(terasLive.temps.t1, thr)}
              title={
                <NameEditor
                  label={seriesLabel("t1", roms, names)}
                  rom={TERAS_NAME_KEY}
                  onSave={saveName}
                />
              }
              value={`${fmt1(terasLive.temps.t1)} °C`}
              t={terasLive.t}
            />
          )}
          {terasLive && terasLive.hum?.rh1 !== undefined && (
            <TerasCard
              level="ok"
              title={<span className="font-semibold">{seriesLabel("rh1", roms, names)}</span>}
              value={`${Math.round(terasLive.hum.rh1)} %`}
              t={terasLive.t}
            />
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs opacity-80 mb-4">
        Peringatan: kuning ≥
        <input
          defaultValue={thr.warn}
          key={"w" + thr.warn}
          onBlur={(e) => saveThr("warn", e.target.value)}
          inputMode="decimal"
          className="w-14 rounded border border-slate-500/50 bg-transparent px-1 text-sm"
        />
        °C · merah ≥
        <input
          defaultValue={thr.crit}
          key={"c" + thr.crit}
          onBlur={(e) => saveThr("crit", e.target.value)}
          inputMode="decimal"
          className="w-14 rounded border border-slate-500/50 bg-transparent px-1 text-sm"
        />
        °C
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={`${btn} ${range === r ? "bg-slate-500/30 font-semibold" : ""}`}
          >
            {RANGE_LABEL[r]}
          </button>
        ))}
        <select
          value={day}
          disabled={range === "7d"}
          onChange={(e) => setDay(e.target.value)}
          className="rounded border border-slate-500/50 bg-transparent px-2 py-1 text-sm disabled:opacity-40"
        >
          {days.map((d) => (
            <option key={d} value={d} className="text-black">
              {d.slice(6)}/{d.slice(4, 6)}/{d.slice(0, 4)}
              {d === days[0] ? " (hari ini)" : ""}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => setTick((t) => t + 1)} className={btn}>
          <i className="fas fa-rotate mr-1" />
          Refresh
        </button>
        <span className="text-xs opacity-60">
          {lastRow
            ? `Terakhir diperbarui ${wibClock(lastRow.t)} WIB`
            : status === "ok"
            ? "Belum ada riwayat"
            : ""}
        </span>
      </div>

      {status === "error" && (
        <div className="rounded border border-red-500/50 bg-red-500/10 p-3 text-sm mb-2">
          Gagal membaca <code>/history/temperature</code>: {error}
          {/permission/i.test(error) && (
            <div className="mt-1 opacity-80">
              Rule RTDB untuk <code>/history</code> belum mengizinkan baca. Rule diatur di
              Firebase console, bukan di repo ini.
            </div>
          )}
        </div>
      )}
      {status === "loading" && <div className="py-10 text-center opacity-70">Memuat…</div>}
      {status === "ok" && keys.length === 0 && hums.length === 0 && (
        <div className="py-10 text-center opacity-70">Tidak ada rekaman pada rentang ini.</div>
      )}

      {status === "ok" && (keys.length > 0 || hums.length > 0) && (
        <div className="select-none">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs px-1 mb-1">
            {keys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => toggle(k)}
                className={hidden.has(k) ? "opacity-40 line-through" : ""}
              >
                <span
                  className="inline-block w-3 mr-1 align-middle"
                  style={{ background: colorOf(k), height: 3 }}
                />
                {nameByKey[k]}
              </button>
            ))}
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--grid)" vertical={false} />
                <XAxis
                  type="number"
                  dataKey="t"
                  domain={[win[0], win[1]]}
                  allowDataOverflow
                  ticks={ticksFor(win[0], win[1])}
                  tickFormatter={
                    range === "7d"
                      ? (t: number) => `${new Date((t + 7 * 3600) * 1000).getUTCDate()}/${hhmm(t)}`
                      : hhmm
                  }
                  tick={{ fontSize: 11 }}
                  stroke="var(--grid)"
                />
                <YAxis
                  width={44}
                  tick={{ fontSize: 11 }}
                  stroke="var(--grid)"
                  unit="°"
                  domain={["auto", "auto"]}
                />
                {keys.map((k) => (
                  <Line
                    key={k}
                    dataKey={k}
                    hide={hidden.has(k)}
                    stroke={colorOf(k)}
                    dot={false}
                    strokeWidth={1.8}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
                <Tooltip isAnimationActive={false} content={<Tip names={nameByKey} />} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {hums.length > 0 && (
            <>
              <div className="text-xs px-1 mt-3 mb-1">
                <span
                  className="inline-block w-3 mr-1 align-middle"
                  style={{ background: "var(--c7)", height: 3 }}
                />
                {nameByKey[hums[0]]}
              </div>
              <div style={{ height: 150 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="var(--grid)" vertical={false} />
                    <XAxis
                      type="number"
                      dataKey="t"
                      domain={[win[0], win[1]]}
                      allowDataOverflow
                      ticks={ticksFor(win[0], win[1])}
                      tickFormatter={hhmm}
                      tick={{ fontSize: 11 }}
                      stroke="var(--grid)"
                    />
                    <YAxis width={44} tick={{ fontSize: 11 }} stroke="var(--grid)" unit="%" domain={[0, 100]} />
                    {hums.map((k) => (
                      <Line
                        key={k}
                        dataKey={k}
                        stroke="var(--c7)"
                        dot={false}
                        strokeWidth={1.8}
                        connectNulls={false}
                        isAnimationActive={false}
                      />
                    ))}
                    <Tooltip isAnimationActive={false} content={<Tip names={nameByKey} />} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          <table className="w-full text-sm mt-3">
            <thead className="text-xs opacity-60 text-left">
              <tr>
                <th className="py-1">Sensor</th>
                <th>Min</th>
                <th>Maks</th>
                <th>Rata-rata</th>
              </tr>
            </thead>
            <tbody>
              {[...keys, ...hums].map((k) => {
                const st = statsFor(statsSrc(k), k);
                const f = (x: number) => (isHumKey(k) ? `${Math.round(x)} %` : `${fmt1(x)} °C`);
                return (
                  <tr key={k} className="border-t border-slate-500/30">
                    <td className="py-1">{nameByKey[k]}</td>
                    <td>{st ? f(st.min) : "--"}</td>
                    <td>{st ? f(st.max) : "--"}</td>
                    <td>{st ? f(st.avg) : "--"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-xs opacity-60 mt-1">
            Garis putus saat sensor putus atau data berjeda &gt; 90 detik. Sumbu waktu WIB.
          </div>
          {range === "7d" && (
            <div className="text-xs opacity-60">
              Data Teras (suhu/kelembapan, per 5 detik) tidak dimuat untuk 7 hari karena ukurannya
              besar; pilih 24 jam atau lebih pendek.
            </div>
          )}
          {terasErr && (
            <div className="text-xs text-red-400">
              Gagal membaca <code>/history/teras</code>: {terasErr}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 rounded border border-slate-500/50 p-3 text-sm">
        <div className="font-semibold mb-1">Reset board</div>
        <p className="text-xs opacity-70 mb-2">
          Setelah sensor ditambah atau dilepas, board perlu di-reset agar daftar sensor
          dibaca ulang. Hanya berfungsi di jaringan lokal.
        </p>
        {boards.length === 0 ? (
          <div className="text-xs opacity-70">
            Belum ada source dengan IP. Isi kolom ip di menu Sources.
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={board?.[0] ?? ""}
              onChange={(e) => setBoardId(e.target.value)}
              className="rounded border border-slate-500/50 bg-transparent px-2 py-1 text-sm"
            >
              {boards.map(([id, s]) => (
                <option key={id} value={id} className="text-black">
                  {s.label} ({s.ip})
                </option>
              ))}
            </select>
            <button
              type="button"
              className={btn}
              onClick={() => {
                setResult(null);
                setConfirm(true);
              }}
            >
              <i className="fas fa-power-off mr-1" />
              Reset board
            </button>
          </div>
        )}
        {result && (
          <div className={`mt-2 text-xs ${result.ok ? "text-green-500" : "text-red-400"}`}>
            {result.msg}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirm}
        title="Reset board?"
        confirmLabel="Reset"
        busy={busy}
        onConfirm={reset}
        onCancel={() => setConfirm(false)}
      >
        Board <b>{board?.[1].label}</b> ({board?.[1].ip}) akan restart dan semua perangkat di
        board itu terputus beberapa detik.
        {mixed && (
          <div className="text-yellow-500">
            App dibuka lewat HTTPS; browser kemungkinan memblokir permintaan http:// ke board.
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
